import { Show } from "solid-js";
import { View, Text, hexColor } from "@lightningtv/solid";
import { fixImageUrl } from "../../utils/fixImageUrl";

// Cards for the "Live TV Channels" and "Popular Movies" rows, shared by the
// residential and hotel home screens.

export const ChannelCard = (props: { channel: any; onEnter: () => void }) => {
  const channel = props.channel;
  return (
    <View
      onEnter={props.onEnter}
      style={{
        width: 350,
        height: 280,
        borderRadius: 20,
        border: { width: 2, color: hexColor("#00000000") },
        color: hexColor("#00000000"),
        focus: {
          border: { width: 6, color: hexColor("#FFFFFF") }
        }
      }}
    >
      {/* Classic card: one container holding image + content block */}
      <View
        skipFocus
        width={340}
        height={270}
        x={5}
        y={5}
        color={hexColor("#2A2A2A")}
        borderRadius={16}
      >
        {/* Image (top) */}
        <View
          skipFocus
          width={340}
          height={191}
          color={hexColor("#1E1E1E")}
          borderRadius={16}
          style={{
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0
          }}
        >
          <Show
            when={channel.stream_icon}
            fallback={
              <Text
                skipFocus
                y={62}
                width={340}
                contain="width"
                textAlign="center"
                fontSize={56}
                color={hexColor("#666666")}
              >
                📺
              </Text>
            }
          >
            <View
              skipFocus
              src={fixImageUrl(channel.stream_icon)}
              width={180}
              height={130}
              x={80}
              y={30}
              style={{ objectFit: "contain" }}
            />
          </Show>
        </View>

        {/* Content block (below image, same width, inside the container) */}
        <Text
          skipFocus
          x={20}
          y={206}
          fontSize={24}
          color={hexColor("#FFFFFF")}
          contain="width"
          width={300}
          style={{ fontWeight: "bold", maxLines: 1 }}
        >
          {channel.name || "Unknown Channel"}
        </Text>
        <Text
          skipFocus
          x={20}
          y={238}
          fontSize={18}
          color={hexColor("#B0B0B0")}
          contain="width"
          width={300}
          style={{ maxLines: 1 }}
        >
          {channel.num ? `Channel ${channel.num}` : "Live TV"}
        </Text>
      </View>
    </View>
  );
};

// Same icons as the Services panel uses when a service has no image
const SERVICE_CATEGORY_ICONS: Record<string, string> = {
  Transport: "🚗",
  Recreation: "🏊",
  Food: "🍽️",
  Spa: "💆",
  Fitness: "💪",
  Business: "💼",
};

// Same size and layout as ChannelCard: image on top, name and category below
export const ServiceCard = (props: { service: any; onEnter: () => void }) => {
  const service = props.service;
  const image = service.service_image || service.service_icon;
  return (
    <View
      onEnter={props.onEnter}
      style={{
        width: 350,
        height: 280,
        borderRadius: 20,
        border: { width: 2, color: hexColor("#00000000") },
        color: hexColor("#00000000"),
        focus: {
          border: { width: 6, color: hexColor("#FFFFFF") }
        }
      }}
    >
      <View
        skipFocus
        width={340}
        height={270}
        x={5}
        y={5}
        color={hexColor("#2A2A2A")}
        borderRadius={16}
      >
        <View
          skipFocus
          width={340}
          height={191}
          color={hexColor("#1E1E1E")}
          borderRadius={16}
          style={{
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0
          }}
        >
          <Show
            when={image}
            fallback={
              <Text
                skipFocus
                y={62}
                width={340}
                contain="width"
                textAlign="center"
                fontSize={56}
              >
                {SERVICE_CATEGORY_ICONS[service.service_category] || "📋"}
              </Text>
            }
          >
            <View
              skipFocus
              src={fixImageUrl(image)}
              width={340}
              height={191}
              borderRadius={16}
              style={{
                objectFit: "cover",
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0
              }}
            />
          </Show>
        </View>

        <Text
          skipFocus
          x={20}
          y={206}
          fontSize={24}
          color={hexColor("#FFFFFF")}
          contain="width"
          width={300}
          style={{ fontWeight: "bold", maxLines: 1 }}
        >
          {service.service_name || "Service"}
        </Text>
        <Text
          skipFocus
          x={20}
          y={238}
          fontSize={18}
          color={hexColor("#B0B0B0")}
          contain="width"
          width={300}
          style={{ maxLines: 1 }}
        >
          {service.service_category || service.service_menu || ""}
        </Text>
      </View>
    </View>
  );
};

// onOpen gets the movie's stream id and category id (falling back to
// fallbackCategoryId), and is only called when both are known.
export const MovieCard = (props: {
  movie: any;
  fallbackCategoryId?: string;
  onOpen: (streamId: string, categoryId: string) => void;
}) => {
  const movie = props.movie;
  const movieData = movie.data || movie;
  const posterSrc = movie.src || movie.stream_icon || movieData.stream_icon || "/assets/movie-placeholder.png";
  const movieName = movieData.name || movieData.title || movie.shortTitle || "Unknown";
  const streamId = movieData.stream_id || movie.entityInfo?.id;
  const categoryId = movieData.category_id || movie.entityInfo?.category_id || props.fallbackCategoryId;

  const year = movieData.year || movieData.release_date?.split("-")[0] || "N/A";
  const genre = movieData.genre?.split(",")[0]?.trim() || "Movie";
  const runtime = movieData.episode_run_time
    ? `${movieData.episode_run_time}m`
    : "N/A";

  return (
    <View
      onEnter={() => {
        if (streamId && categoryId) {
          props.onOpen(streamId, categoryId);
        }
      }}
      style={{
        width: 350,
        height: 490,
        borderRadius: 16,
        x: -10,
        y: -10,
        border: { width: 2, color: hexColor("#00000000") },
        color: hexColor("#00000000"),
        focus: {
          border: { width: 20, color: hexColor("#FFFFFF") }
        }
      }}
    >
      {/* Card Container */}
      <View
        width={340}
        height={480}
        x={5}
        y={5}
        color={hexColor("#2A2A2A")}
        borderRadius={16}
      >
        {/* Movie Poster */}
        <View
          src={fixImageUrl(posterSrc)}
          width={340}
          height={480}
          borderRadius={16}
          style={{
            objectFit: "cover",
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0
          }}
        />

        {/* Light overlay over the poster */}
        <View
          skipFocus
          width={340}
          height={480}
          borderRadius={16}
          color={hexColor("#00000070")}
        />

        <View
          y={320}
          width={340}
          height={160}
          src="assets/gradient.png"
          borderRadius={16}
          style={{
            borderBottomLeftRadius: 16,
            borderBottomRightRadius: 16
          }}
        >
          {/* Movie Title - Split if too long */}
          <Show
            when={movieName.length <= 40}
            fallback={
              <>
                <Text
                  x={20}
                  y={70}
                  fontSize={22}
                  color={hexColor("#FFFFFF")}
                  contain="width"
                  width={300}
                  style={{
                    fontWeight: "bold"
                  }}
                >
                  {movieName.substring(0, 20)}
                </Text>
                <Text
                  x={20}
                  y={90}
                  fontSize={22}
                  color={hexColor("#FFFFFF")}
                  contain="width"
                  width={300}
                  style={{
                    fontWeight: "bold"
                  }}
                >
                  {movieName.substring(20, 40) + (movieName.length > 40 ? "..." : "")}
                </Text>
              </>
            }
          >
            <Text
              x={20}
              y={70}
              fontSize={22}
              color={hexColor("#FFFFFF")}
              contain="width"
              width={300}
              style={{
                fontWeight: "bold"
              }}
            >
              {movieName}
            </Text>
          </Show>

          {/* Movie Metadata - Closer when single line */}
          <Text
            x={20}
            y={movieName.length <= 30 ? 105 : 130}
            fontSize={18}
            color={hexColor("#888888")}
            contain="width"
            width={300}
            maxLines={1}
          >
            {year} • {genre} • {runtime}
          </Text>
        </View>
      </View>
    </View>
  );
};
